<template>
    <button
        ref="newsFeedLauncher"
        v-b-tooltip.hover.topleft="$t('Notifications')"
        type="button"
        data-cy="notification-btn"
        class="btn btn-link text-decoration-none pb-0"
        @click.stop="onClick"
    >
        <span
            class="label-pos"
            :class="{ 'attention-marker-red': isAttentionNeeded }"
        >
            <i class="fd-alert text-dark"></i>
        </span>
    </button>
</template>

<script>
import { productFruits } from 'product-fruits';
import { mapGetters } from 'vuex';

export default {
    name: 'NotificationsButton',

    compatConfig: {
        INSTANCE_EVENT_EMITTER: true, // bv-tooltip
    },

    data() {
        return {
            isNewsWidgetAttached: false,
            isUnreadCountListenerAttached: false,
            unreadNewsFeedCount: 0,
        };
    },

    computed: {
        ...mapGetters(['userProfile', 'selectedPlace', 'selectedCompany']),
        isAttentionNeeded() {
            return this.unreadNewsFeedCount > 0;
        },
    },

    watch: {
        userProfile() {
            this.initializeNewsFeedLauncher();
        },
        selectedCompany() {
            this.initializeNewsFeedLauncher();
        },
        selectedPlace() {
            this.initializeNewsFeedLauncher();
        },
    },

    mounted() {
        this.initializeNewsFeedLauncher();
    },

    methods: {
        ensureProductFruitsReady() {
            if (!process.env.PRODUCT_FRUITS_WORKSPACE_CODE) {
                return false;
            }

            if (window.productFruitsIsReady || window.$productFruits) {
                return true;
            }

            const username = this.userProfile?.username || `${this.userProfile?.id || ''}`;
            if (!username) {
                return false;
            }

            const company = this.selectedCompany?.company;

            if (process.env.MIX_APP_ENV !== 'local' && this.userProfile?.id) {
                productFruits.init(
                    process.env.PRODUCT_FRUITS_WORKSPACE_CODE,
                    'en' || this.$i18n?.locale?.substring(0, 2),
                    {
                        username,
                        email: this.userProfile?.username || '',
                        signUpAt: company?.created_at,
                        role: company?.subscription?.price_packages[0]?.price_package?.name ?? 'Churned',
                        props: {
                            country: this.selectedPlace?.country?.code || '',
                            locations_amount: company?.locations_amount,
                            user_rights: this.selectedPlace?.role ?? company?.role,
                        },
                    }
                );
                return true;
            }

            return false;
        },
        initializeNewsFeedLauncher() {
            if (!(this.userProfile.id && this.selectedCompany?.company?.id && this.selectedPlace?.country?.code)) {
                return;
            }
            const launcherElement = this.$refs.newsFeedLauncher;
            if (!launcherElement || !this.ensureProductFruitsReady()) {
                return;
            }

            productFruits.safeExec(() => {
                const newsFeedApi = window.productFruits?.api?.announcementsV2;
                if (!this.isUnreadCountListenerAttached && typeof newsFeedApi?.listen === 'function') {
                    newsFeedApi.listen('newsfeed-unread-count-changed', (data) => {
                        this.unreadNewsFeedCount = Number(data?.count) || 0;
                    });
                    this.isUnreadCountListenerAttached = true;
                }

                if (!this.isNewsWidgetAttached && typeof newsFeedApi?.attachNewsWidgetToElement === 'function') {
                    // Attach ProductFruits widget to this button so click toggles the native NewsFeed popup.
                    newsFeedApi.attachNewsWidgetToElement(launcherElement);
                    this.isNewsWidgetAttached = true;
                }
            });
        },
        onClick() {
            this.initializeNewsFeedLauncher();
        },
    },
};
</script>

<style lang="scss" scoped>
.label-pos {
    &:before,
    &:after {
        top: 0%;
        left: 100%;
    }
}
</style>
